import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Save, RefreshCw } from "lucide-react";
import { apiBaseUrl, authHeaders, isSuperAdmin } from "./shared";

// Real, manually-set fleet cost inputs -- these fields aren't wired into
// any cost calculation until a real admin enters actual numbers, same
// deliberate follow-up as always. Alert thresholds, matching radius,
// feature toggles, and fuel-optimization knobs live on the separate
// Fleet Operational Settings page (FleetOperationalSettings.jsx) -- this
// page is Cost Inputs + hard spend ceilings only, split out so "what does
// a load really cost" and "how does the platform behave" aren't mixed on
// one screen. Backed by plain system_properties rows,
// read/written through the generic dynamic_api table endpoint like any
// other table -- no dedicated backend route needed for a settings form.
//
// type drives both rendering (currency shows a "$" prefix, boolean shows
// a toggle switch instead of a number input) and validation (only
// currency/number fields need the NaN check). section groups fields into
// their own card with a heading, in FIELD_SECTIONS order.
//
// WHEN ADDING A NEW NON-protected key here: admin's system_properties ACL
// row is an enumerated sip_name whitelist (the script_condition DSL only
// supports "=", not a fleet.%-style prefix match), not "any fleet.* key" --
// a new key added only here and not to that whitelist renders as 0 for
// admin (see 0034 and 0067's own migration docstrings, this exact bug
// confirmed real twice). Add a migration widening
// access_control_rules.script_condition for the admin role_id at the same
// time you add a field here.
const FIELDS = [
  {
    key: "fleet.insurance_cost_per_mile", label: "Insurance", type: "currency", unit: "per mile",
    hint: "Per-mile insurance cost folded into every load's Equipment cost line across trip costing, matching, and profit reports. Set it to what this fleet actually pays per mile in premiums (e.g. 0.30) -- raising it makes every load look correctly less profitable, it isn't a rounding knob.",
    section: "Cost Inputs",
  },
  {
    key: "fleet.office_payroll_cost_per_mile", label: "Office payroll & expenses", type: "currency", unit: "per mile",
    hint: "Per-mile share of dispatch/back-office payroll and overhead, allocated to every load the same way insurance is. Same units as the others (e.g. 0.30, not 30) -- this is what keeps a load's reported profit honest about the real cost of running the office, not just the truck.",
    section: "Cost Inputs",
  },
  {
    key: "fleet.maintenance_cost_per_mile", label: "Maintenance", type: "currency", unit: "per mile",
    hint: "Per-mile wear-and-tear reserve (tires, oil, brakes, unscheduled repairs) -- a real, ongoing cost of putting miles on a truck, not a one-time expense. Set it from this fleet's actual maintenance spend divided by its actual miles, not a guess.",
    section: "Cost Inputs",
  },
  {
    key: "fleet.equipment_price_per_week", label: "Equipment price", type: "currency", unit: "per week",
    hint: "The real weekly cost of owning or leasing one truck+trailer (lease/finance payment, permits, depreciation). This is prorated to 1/7th per day and folded into every load's own cost, so dispatch can see load-by-load whether a truck is on pace to cover its weekly equipment cost -- it also drives the Weekly Equipment Cost Coverage widget on Reports and the Truck Economics weekly-balance view.",
    section: "Cost Inputs",
  },
  {
    key: "fleet.wait_time_cost_per_hour", label: "Wait time cost", type: "currency", unit: "per hour",
    hint: "What an hour of a truck sitting idle at a pickup or delivery really costs -- the opportunity cost of what that truck could be earning elsewhere, not just detention pay. Used to price wait time into route comparisons and to flag pickup-timing-uncertain candidates during broker-call matching.",
    section: "Cost Inputs",
  },

  {
    key: "fleet.min_guaranteed_pay_miles", label: "Minimum guaranteed pay miles", type: "number", unit: "miles",
    hint: "A per-mile driver booked on a load shorter than this still gets paid as if it were this many miles (floor_miles x their per-mile rate), not the real shorter distance x rate -- a livable-minimum-payout guarantee. Only affects driver pay; fuel, insurance, office, and maintenance costs always use the real miles driven. Doesn't apply to hourly or fixed-daily drivers. Read by Check Load, Log Broker Call, Find Next Load, and the Cost Engine, and persisted into every route plan a dispatcher saves -- so it also flows into Truck Economics and Reports for any load booked after this is set.",
    section: "Cost Inputs",
  },
  {
    key: "fleet.min_guaranteed_pay_uses_total_miles", label: "Guarantee applies to total miles (incl. deadhead)", type: "boolean",
    hint: "Off (default): the floor above applies to the load's own loaded miles only -- deadhead to reach it is always paid at its real distance, on top. On: the floor applies to the driver's whole trip (deadhead + loaded) instead -- a truck that deadheads 400 miles to pick up a 50-mile load would NOT get the floor topped up further in that case, since the combined total already clears it.",
    section: "Cost Inputs",
  },

  // protected: true -- these are hard spend ceilings, not operational
  // inputs. Saved through PUT /system_properties/{id}/super-admin-only
  // (server-side gated to super_admin) instead of the plain generic
  // endpoint every other field above uses, and this whole section only
  // renders for a super_admin in the first place (see VISIBLE_FIELDS
  // below) -- hiding it from a plain admin is the UI convenience, the
  // backend gate is what actually stops them.
  {
    key: "here.daily_cost_cap_usd", label: "HERE routing/toll/geocode cap", type: "currency", unit: "/ day",
    hint: "Per-agent-process daily ceiling on HERE routing, toll, and geocoding spend (broker-call matching, trip planning, route history). Once today's logged HERE routing spend hits this, those calls start failing with a clear cap-hit error until tomorrow -- it does NOT cover map tile spend, see the combined monthly cap below for the real total ceiling.",
    section: "Spend Ceilings", protected: true,
  },
  {
    key: "here.monthly_cost_cap_usd", label: "HERE total monthly cap", type: "currency", unit: "/ month",
    hint: "The real ceiling: total HERE spend this month across EVERYTHING combined -- routing, tolls, geocoding, AND map tiles. Once hit, map tiles transparently fall back to OpenStreetMap for the rest of the month (map stays up, just less polished) and routing/toll/geocode calls start failing until next month. This is the number that answers \"what's the most HERE can cost us this month\".",
    section: "Spend Ceilings", protected: true,
  },
  {
    key: "agents.daily_cost_cap_usd", label: "Claude agent cap (per agent)", type: "currency", unit: "/ day",
    hint: "Daily Claude spend ceiling PER AI agent (load intake, fuel optimization, profit optimization run independently). With 3 active agents this is not a total budget -- see the combined monthly cap below for that.",
    section: "Spend Ceilings", protected: true,
  },
  {
    key: "agents.monthly_cost_cap_usd", label: "Claude total monthly cap", type: "currency", unit: "/ month",
    hint: "The real ceiling: combined Claude spend across ALL agents this month. Once hit, every agent is blocked from running (not just the busiest one) until next month or the cap is raised. This is the number that answers \"what's the most the Claude API can cost us this month\".",
    section: "Spend Ceilings", protected: true,
  },
];

const FIELD_SECTIONS = ["Cost Inputs", "Spend Ceilings"];

function defaultDraft(f) {
  return f.type === "boolean" ? "true" : "0";
}

export default function FleetCostSettings() {
  // Recomputed per render (cheap localStorage read) rather than memoized --
  // same pattern DispatcherBoard.jsx already uses for its own role checks.
  const superAdmin = isSuperAdmin();
  const VISIBLE_FIELDS = FIELDS.filter((f) => !f.protected || superAdmin);
  const [rows, setRows] = useState({}); // sip_name -> { id, sip_value }
  const [drafts, setDrafts] = useState({}); // sip_name -> string being edited
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${apiBaseUrl}/system_properties/?limit=500`, authHeaders())
      .then((res) => {
        const records = res.data.records || res.data || [];
        const bySipName = {};
        records.forEach((r) => {
          if (FIELDS.some((f) => f.key === r.sip_name)) bySipName[r.sip_name] = r;
        });
        setRows(bySipName);
        const nextDrafts = {};
        FIELDS.forEach((f) => {
          nextDrafts[f.key] = bySipName[f.key]?.sip_value ?? defaultDraft(f);
        });
        setDrafts(nextDrafts);
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirtyKeys = FIELDS.filter((f) => drafts[f.key] !== (rows[f.key]?.sip_value ?? defaultDraft(f))).map((f) => f.key);
  const hasInvalid = dirtyKeys.some((key) => {
    const f = FIELDS.find((x) => x.key === key);
    if (f.type === "boolean") return false;
    return drafts[key] === "" || Number.isNaN(Number(drafts[key]));
  });

  const save = () => {
    if (!dirtyKeys.length || hasInvalid) return;
    // A dirty key with no matching row means the initial load either hasn't
    // finished yet or failed silently for that property -- saving anyway
    // would crash on row.id below (real bug hit in production: a toggle
    // flipped and saved before/without its row ever loading). Bail with a
    // clear message instead of a blank-screen exception.
    const missingRows = dirtyKeys.filter((key) => !rows[key]);
    if (missingRows.length) {
      setError("Some properties haven't finished loading yet -- refresh the page and try again.");
      return;
    }
    setSaving(true);
    setError(null);
    Promise.all(
      dirtyKeys.map((key) => {
        const f = FIELDS.find((x) => x.key === key);
        const row = rows[key];
        const value = f.type === "boolean" ? drafts[key] : String(Number(drafts[key]));
        // protected fields (hard spend ceilings) save through the
        // super_admin-gated endpoint -- see SUPER_ADMIN_ONLY_PROPERTIES
        // in main.py, the actual enforcement point.
        const url = f.protected
          ? `${apiBaseUrl}/system_properties/${row.id}/super-admin-only`
          : `${apiBaseUrl}/system_properties/${row.id}`;
        return axios.put(url, { sip_value: value }, authHeaders());
      })
    )
      .then(() => {
        setSavedAt(new Date());
        load();
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setSaving(false));
  };

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Fleet Cost Settings</h1>
          <div style={styles.subtitle}>
            Real per-mile/per-week cost inputs used across trip costing, negotiation floors, and profit
            reports, plus the hard HERE/Claude spend ceilings -- update these whenever the underlying
            real-world numbers change. Alert thresholds, matching, and fuel optimization live on{" "}
            <a href="/dispatch/fleet-operational-settings" style={{ color: "var(--brand)" }}>Fleet Operational Settings</a>.
          </div>
        </div>
        <button style={styles.refreshBtn} onClick={load} disabled={loading}>
          <RefreshCw size={13} style={loading ? { animation: "lv-spin 0.8s linear infinite" } : undefined} /> Refresh
        </button>
      </div>

      {error && <div style={styles.errorBox}>Couldn't save: {error}</div>}

      {FIELD_SECTIONS.map((section) => {
        const sectionFields = VISIBLE_FIELDS.filter((f) => f.section === section);
        if (!sectionFields.length) return null;
        return (
          <div key={section} style={styles.sectionBlock}>
            <div style={styles.sectionLabel}>{section}</div>
            <div style={styles.card}>
              {sectionFields.map((f, i) => {
                const draft = drafts[f.key] ?? defaultDraft(f);
                const isDirty = draft !== (rows[f.key]?.sip_value ?? defaultDraft(f));
                const isInvalid = f.type !== "boolean" && (draft === "" || Number.isNaN(Number(draft)));
                const isOn = draft === "true";
                return (
                  <div
                    key={f.key}
                    style={{ ...styles.row, borderBottom: i === sectionFields.length - 1 ? "none" : "1px solid var(--line)" }}
                  >
                    <div style={styles.rowTop}>
                      <div style={styles.rowLabel}>{f.label}</div>
                      {f.type === "boolean" ? (
                        <button
                          type="button"
                          onClick={() => setDrafts((d) => ({ ...d, [f.key]: isOn ? "false" : "true" }))}
                          disabled={loading || saving}
                          style={{ ...styles.toggleTrack, background: isOn ? "var(--brand)" : "var(--line)" }}
                          aria-pressed={isOn}
                        >
                          <span style={{ ...styles.toggleThumb, transform: isOn ? "translateX(16px)" : "translateX(0)" }} />
                        </button>
                      ) : (
                        <div style={styles.inputGroup}>
                          {f.type === "currency" && <span style={styles.dollarSign}>$</span>}
                          <input
                            type="number"
                            step="0.01"
                            value={draft}
                            onChange={(e) => setDrafts((d) => ({ ...d, [f.key]: e.target.value }))}
                            style={{ ...styles.input, borderColor: isInvalid ? "#dc2626" : isDirty ? "var(--brand)" : "var(--line)" }}
                            disabled={loading || saving}
                          />
                          <span style={styles.unit}>{f.unit}</span>
                        </div>
                      )}
                    </div>
                    {f.hint && <div style={styles.rowHint}>{f.hint}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div style={styles.footer}>
        <button
          style={{ ...styles.saveBtn, opacity: !dirtyKeys.length || hasInvalid || saving ? 0.5 : 1 }}
          onClick={save}
          disabled={!dirtyKeys.length || hasInvalid || saving}
        >
          <Save size={14} /> {saving ? "Saving…" : "Save changes"}
        </button>
        {!dirtyKeys.length && savedAt && (
          <span style={styles.savedNote}>Saved at {savedAt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>
        )}
        {dirtyKeys.length > 0 && !hasInvalid && (
          <span style={styles.dirtyNote}>{dirtyKeys.length} unsaved change{dirtyKeys.length > 1 ? "s" : ""}</span>
        )}
      </div>
    </div>
  );
}

const styles = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 16 },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4, maxWidth: 560, lineHeight: 1.5 },
  refreshBtn: {
    display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "var(--ink-2)",
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: 8, padding: "7px 14px", cursor: "pointer",
    flexShrink: 0,
  },
  errorBox: {
    background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", color: "#dc2626",
    borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 13, marginBottom: 16,
  },
  sectionBlock: { maxWidth: 760, marginBottom: 22 },
  sectionLabel: {
    fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase",
    letterSpacing: 0.4, marginBottom: 8,
  },
  card: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
    boxShadow: "var(--shadow-xs)", maxWidth: 760, overflow: "hidden",
  },
  row: { padding: "16px 20px" },
  rowTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 },
  rowLabel: { fontSize: 13.5, fontWeight: 600, color: "var(--ink)" },
  rowHint: { fontSize: 12, color: "var(--muted)", marginTop: 6, lineHeight: 1.55, maxWidth: 640 },
  toggleTrack: {
    position: "relative", width: 36, height: 20, borderRadius: 999, border: "none", cursor: "pointer",
    padding: 2, flexShrink: 0,
  },
  toggleThumb: {
    display: "block", width: 16, height: 16, borderRadius: "50%", background: "#fff",
    transition: "transform 0.15s ease", boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
  },
  inputGroup: { display: "flex", alignItems: "center", gap: 6, flexShrink: 0 },
  dollarSign: { fontSize: 13, color: "var(--muted)", fontWeight: 600 },
  input: {
    width: 90, fontSize: 13.5, fontWeight: 600, color: "var(--ink)", padding: "6px 8px",
    border: "1px solid var(--line)", borderRadius: 7, textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  unit: { fontSize: 12, color: "var(--muted)", width: 62 },
  footer: { display: "flex", alignItems: "center", gap: 12, marginTop: 16 },
  saveBtn: {
    display: "flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 700, color: "#fff",
    background: "var(--brand)", border: "none", borderRadius: 8, padding: "9px 18px", cursor: "pointer",
  },
  savedNote: { fontSize: 12, color: "#4cae6d", fontWeight: 600 },
  dirtyNote: { fontSize: 12, color: "var(--muted)" },
};
